import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import Tooltip from "@mui/material/Tooltip";
import Select from "react-select";
import DatePicker from "react-datepicker";
import { IoMdArrowBack } from "react-icons/io";
import { format } from "date-fns";
import "react-datepicker/dist/react-datepicker.css";
import "./IsolationPermitStepPage.css";
import {
  createIsolationPermit,
  fetchEquipmentTagOptions,
  fetchDepartmentOptions,
  fetchIsolationPermit,
  fetchIsolationPermitTypes,
  submitEnergisationCertificate,
  submitEnergisationRequest,
  submitIsolationCertificate,
  updateIsolationRequest,
} from "./isolationPermitApi";

const STEP_CONFIG = {
  request: {
    title: "Request For Isolation",
    nextPath: (permitId) => `/isolation-certificate/${permitId}`,
    allowedStatuses: ["Isolation_Requested"],
  },
  isolation_certificate: {
    title: "Certificate of Isolation",
    nextPath: (permitId) => `/request-energisation/${permitId}`,
    allowedStatuses: ["Isolation_Requested"],
  },
  energisation_request: {
    title: "Request For Energisation",
    nextPath: (permitId) => `/energisation-certificate/${permitId}`,
    allowedStatuses: ["Isolation_Certified"],
  },
  energisation_certificate: {
    title: "Certificate of Energisation",
    nextPath: (permitId) =>
      `/view-pdf-formate/${permitId}/${encodeURIComponent(
        "Electrical Isolation/Energisation",
      )}/wp_requester`,
    allowedStatuses: ["Energisation_Requested"],
  },
};

const ISOLATION_ACTION_OPTIONS = [
  { value: "switches", label: "Switches" },
  { value: "isolators", label: "Isolators" },
  { value: "links", label: "Links" },
  { value: "fuses", label: "Fuses" },
];

const STATUS_STYLES = {
  Isolation_Requested: { color: "#1d4ed8", background: "#dbeafe" },
  Isolation_Certified: { color: "#166534", background: "#dcfce7" },
  Energisation_Requested: { color: "#92400e", background: "#fef3c7" },
  Energisation_Certified: { color: "#7c2d12", background: "#ffedd5" },
};

const getApiErrorMessage = (error, fallback) =>
  error?.response?.data?.message || fallback;

const formatForApi = (date) =>
  date ? format(date, "yyyy-MM-dd HH:mm:ss") : "";

const parseApiDate = (value) => {
  if (!value) return null;
  const normalized = String(value).replace(" ", "T");
  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatStatusLabel = (status) =>
  status ? status.replaceAll("_", " ") : "NA";

const ISOLATION_LIST_ROUTE = "/isolation-permits";
const selectA11yProps = {
  screenReaderStatus: () => "",
  ariaLiveMessages: {
    guidance: () => "",
    onChange: () => "",
    onFilter: () => "",
    onFocus: () => "",
  },
};

function IsolationPermitStepPage({ isSidebarExpanded, step }) {
  const navigate = useNavigate();
  const { permitId } = useParams();
  const config = STEP_CONFIG[step];

  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(Boolean(permitId));
  const [permitTypeOptions, setPermitTypeOptions] = useState([]);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [equipmentTagOptions, setEquipmentTagOptions] = useState([]);
  const [permitData, setPermitData] = useState(null);
  const [errors, setErrors] = useState({});

  const [selectedDepartment, setSelectedDepartment] = useState(null);

  const [commonForm, setCommonForm] = useState({
    permit_type_id: "",
    department_id: "",
    section: "",
    facility_location: "",
  });

  const [requestForm, setRequestForm] = useState({
    request_date_time: null,
    department_section: "",
    equipment_number: "",
    equipment_circuit_name: "",
    maintenance_work_by: "",
    requested_by_name: "",
    requested_by_designation: "",
  });

  const [isolationCertForm, setIsolationCertForm] = useState({
    cert_date_time: null,
    certified_equipment_circuit_no: "",
    ka_of: "",
    isolation_actions_taken: [],
    danger_tag_put: false,
    lockout_tag_no: "",
    authorized_person_name: "",
    authorized_person_designation: "",
  });

  const [energisationRequestForm, setEnergisationRequestForm] = useState({
    request_date_time: null,
    department_section: "",
    equipment_number: "",
    equipment_circuit_name: "",
    work_completed_permits_closed: false,
    requested_by_name: "",
    requested_by_designation: "",
  });

  const [energisationCertForm, setEnergisationCertForm] = useState({
    cert_date_time: null,
    certified_equipment_circuit_no: "",
    ka_of: "",
    electrically_energized: false,
    danger_tag_removed: false,
    recorded_in_shift_logbook: false,
    authorized_person_name: "",
    authorized_person_designation: "",
  });

  const showSnackbar = useCallback((message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  }, []);

  const handleSnackbarClose = (_, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const currentStatus = permitData?.permit?.request_status || null;
  const isRequestStep = step === "request";
  const shouldShowDepartmentField = isRequestStep && !permitId;
  const isCurrentStepEditable = useMemo(() => {
    if (step === "request" && !permitId) return true;
    return config.allowedStatuses.includes(currentStatus);
  }, [config.allowedStatuses, currentStatus, permitId, step]);

  const topSummary = useMemo(() => {
    if (!permitData?.permit) return [];

    return [
      {
        label: "Request ID",
        value: permitData.permit.request_id || "NA",
      },
      {
        label: "Permit Type",
        value: permitData.permit.work_permit_name || "NA",
      },
      {
        label: "Department",
        value: permitData.permit.DepartmentName || "NA",
      },
      {
        label: "Section",
        value: permitData.permit.section || "NA",
      },
      {
        label: "Facility Location",
        value: permitData.permit.facility_location || "NA",
      },
      {
        label: "Status",
        value: formatStatusLabel(permitData.permit.request_status),
      },
    ];
  }, [permitData]);

  const loadPermitData = useCallback(async () => {
    if (!permitId) return;

    try {
      setIsLoading(true);
      const payload = await fetchIsolationPermit(permitId);
      setPermitData(payload);

      const permit = payload.permit || {};
      const isolation = payload.isolation_details || {};
      const energisation = payload.energisation_details || {};

      setCommonForm({
        permit_type_id: permit.permit_type_id || "",
        department_id: permit.department_id || "",
        section: permit.section || "",
        facility_location: permit.facility_location || "",
      });

      setRequestForm({
        request_date_time:
          parseApiDate(isolation.request_date) ||
          parseApiDate(isolation.request_time),
        department_section: isolation.department_section_area || "",
        equipment_number: isolation.equipment_number || "",
        equipment_circuit_name: isolation.equipment_circuit_name || "",
        maintenance_work_by: isolation.maintenance_work_by || "",
        requested_by_name: isolation.requested_by_name || "",
        requested_by_designation: isolation.requested_by_designation || "",
      });

      setIsolationCertForm({
        cert_date_time:
          parseApiDate(isolation.cert_date) || parseApiDate(isolation.cert_time),
        certified_equipment_circuit_no:
          isolation.certified_equipment_circuit_no || "",
        ka_of: isolation.ka_of || "",
        isolation_actions_taken: Array.isArray(isolation.isolation_actions_taken)
          ? isolation.isolation_actions_taken
          : [],
        danger_tag_put: Boolean(isolation.danger_tag_put),
        lockout_tag_no: isolation.lockout_tag_no || "",
        authorized_person_name: isolation.authorized_person_name || "",
        authorized_person_designation:
          isolation.authorized_person_designation || "",
      });

      setEnergisationRequestForm({
        request_date_time:
          parseApiDate(energisation.request_date) ||
          parseApiDate(energisation.request_time),
        department_section: energisation.department_section_area || "",
        equipment_number: energisation.equipment_number || "",
        equipment_circuit_name: energisation.equipment_circuit_name || "",
        work_completed_permits_closed: Boolean(
          energisation.work_completed_permits_closed,
        ),
        requested_by_name: energisation.requested_by_name || "",
        requested_by_designation: energisation.requested_by_designation || "",
      });

      setEnergisationCertForm({
        cert_date_time:
          parseApiDate(energisation.cert_date) ||
          parseApiDate(energisation.cert_time),
        certified_equipment_circuit_no:
          energisation.certified_equipment_circuit_no || "",
        ka_of: energisation.ka_of || "",
        electrically_energized: Boolean(energisation.electrically_energized),
        danger_tag_removed: Boolean(energisation.danger_tag_removed),
        recorded_in_shift_logbook: Boolean(
          energisation.recorded_in_shift_logbook,
        ),
        authorized_person_name: energisation.authorized_person_name || "",
        authorized_person_designation:
          energisation.authorized_person_designation || "",
      });
    } catch (error) {
      showSnackbar(
        getApiErrorMessage(error, "Failed to load isolation permit"),
        "error",
      );
    } finally {
      setIsLoading(false);
    }
  }, [permitId, showSnackbar]);

  useEffect(() => {
    const loadLookups = async () => {
      try {
        const [permitTypes, departments, equipmentTags] = await Promise.all([
          fetchIsolationPermitTypes(),
          fetchDepartmentOptions(),
          fetchEquipmentTagOptions(),
        ]);
        setPermitTypeOptions(permitTypes);
        setDepartmentOptions(departments);
        setEquipmentTagOptions(equipmentTags);
      } catch (error) {
        showSnackbar("Failed to load permit lookups", "error");
      }
    };

    loadLookups();
  }, [showSnackbar]);

  useEffect(() => {
    if (!isRequestStep || permitId || commonForm.permit_type_id || !permitTypeOptions.length) {
      return;
    }

    setCommonForm((prev) => ({
      ...prev,
      permit_type_id: permitTypeOptions[0]?.value || "",
    }));
  }, [commonForm.permit_type_id, isRequestStep, permitId, permitTypeOptions]);

  useEffect(() => {
    loadPermitData();
  }, [loadPermitData]);

  useEffect(() => {
    setSelectedDepartment(
      departmentOptions.find(
        (option) => String(option.value) === String(commonForm.department_id),
      ) || null,
    );
  }, [commonForm.department_id, departmentOptions]);

  const clearError = (field) => {
    setErrors((prev) => ({
      ...prev,
      [field]: "",
    }));
  };

  const handleCommonInputChange = (event) => {
    const { name, value } = event.target;
    setCommonForm((prev) => ({
      ...prev,
      [name]: value,
    }));
    clearError(name);
  };

  const handleRequestInputChange = (event) => {
    const { name, value } = event.target;
    setRequestForm((prev) => ({
      ...prev,
      [name]: value,
    }));
    clearError(name);
  };

  const handleIsolationCertInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    setIsolationCertForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    clearError(name);
  };

  const handleEnergisationRequestInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    setEnergisationRequestForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    clearError(name);
  };

  const handleEnergisationCertInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    setEnergisationCertForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    clearError(name);
  };

  const validateRequestStep = () => {
    const nextErrors = {};

    if (!commonForm.permit_type_id) {
      nextErrors.permit_type_id = "Permit type is required";
    }
    if (shouldShowDepartmentField && !commonForm.department_id) {
      nextErrors.department_id = "Department is required";
    }
    if (!commonForm.facility_location.trim()) {
      nextErrors.facility_location = "Facility location is required";
    }
    if (!requestForm.request_date_time) {
      nextErrors.request_date_time = "Request date and time is required";
    }
    if (!requestForm.department_section.trim()) {
      nextErrors.department_section = "Department / section / area is required";
    }
    if (!requestForm.equipment_number.trim()) {
      nextErrors.equipment_number = "Equipment number is required";
    }
    if (!requestForm.equipment_circuit_name.trim()) {
      nextErrors.equipment_circuit_name = "Equipment / circuit name is required";
    }
    if (!requestForm.maintenance_work_by.trim()) {
      nextErrors.maintenance_work_by = "Maintenance work by is required";
    }
    if (!requestForm.requested_by_name.trim()) {
      nextErrors.requested_by_name = "Requested by name is required";
    }
    if (!requestForm.requested_by_designation.trim()) {
      nextErrors.requested_by_designation =
        "Requested by designation is required";
    }

    return nextErrors;
  };

  const validateIsolationCertificateStep = () => {
    const nextErrors = {};

    if (!isolationCertForm.cert_date_time) {
      nextErrors.cert_date_time = "Certificate date and time is required";
    }
    if (!isolationCertForm.certified_equipment_circuit_no.trim()) {
      nextErrors.certified_equipment_circuit_no =
        "Certified equipment / circuit no. is required";
    }
    if (!isolationCertForm.ka_of.trim()) {
      nextErrors.ka_of = "KA of is required";
    }
    if (!isolationCertForm.authorized_person_name.trim()) {
      nextErrors.authorized_person_name =
        "Authorised person name is required";
    }
    if (!isolationCertForm.authorized_person_designation.trim()) {
      nextErrors.authorized_person_designation =
        "Authorised person designation is required";
    }

    return nextErrors;
  };

  const validateEnergisationRequestStep = () => {
    const nextErrors = {};

    if (!energisationRequestForm.request_date_time) {
      nextErrors.energisation_request_date_time =
        "Request date and time is required";
    }
    if (!energisationRequestForm.department_section.trim()) {
      nextErrors.enrg_department_section =
        "Department / section / area is required";
    }
    if (!energisationRequestForm.equipment_number.trim()) {
      nextErrors.enrg_equipment_number = "Equipment number is required";
    }
    if (!energisationRequestForm.equipment_circuit_name.trim()) {
      nextErrors.enrg_equipment_circuit_name =
        "Equipment / circuit name is required";
    }
    if (!energisationRequestForm.requested_by_name.trim()) {
      nextErrors.enrg_requested_by_name = "Requested by name is required";
    }
    if (!energisationRequestForm.requested_by_designation.trim()) {
      nextErrors.enrg_requested_by_designation =
        "Requested by designation is required";
    }

    return nextErrors;
  };

  const validateEnergisationCertificateStep = () => {
    const nextErrors = {};

    if (!energisationCertForm.cert_date_time) {
      nextErrors.energisation_cert_date_time =
        "Certificate date and time is required";
    }
    if (!energisationCertForm.certified_equipment_circuit_no.trim()) {
      nextErrors.enrg_certified_equipment_circuit_no =
        "Certified equipment / circuit no. is required";
    }
    if (!energisationCertForm.ka_of.trim()) {
      nextErrors.enrg_ka_of = "KA of is required";
    }
    if (!energisationCertForm.authorized_person_name.trim()) {
      nextErrors.enrg_authorized_person_name =
        "Authorised person name is required";
    }
    if (!energisationCertForm.authorized_person_designation.trim()) {
      nextErrors.enrg_authorized_person_designation =
        "Authorised person designation is required";
    }

    return nextErrors;
  };

  const handleSave = async () => {
    if (!isCurrentStepEditable) {
      showSnackbar(
        `This step is locked because the permit is currently in '${formatStatusLabel(currentStatus)}' status.`,
        "error",
      );
      return;
    }

    let nextErrors = {};

    if (step === "request") nextErrors = validateRequestStep();
    if (step === "isolation_certificate") {
      nextErrors = validateIsolationCertificateStep();
    }
    if (step === "energisation_request") {
      nextErrors = validateEnergisationRequestStep();
    }
    if (step === "energisation_certificate") {
      nextErrors = validateEnergisationCertificateStep();
    }

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      showSnackbar("Please fill all required fields", "error");
      return;
    }

    try {
      setIsSaving(true);

      let responsePayload = {};
      let finalPermitId = permitId;

      if (step === "request") {
        const payload = {
          permit_type_id: commonForm.permit_type_id,
          department_id: commonForm.department_id,
          section: commonForm.section || "",
          facility_location: commonForm.facility_location || "",
          iso_request_datetime: formatForApi(requestForm.request_date_time),
          iso_department_section: requestForm.department_section,
          iso_equipment_number: requestForm.equipment_number,
          iso_equipment_circuit_name: requestForm.equipment_circuit_name,
          iso_maintenance_work_by: requestForm.maintenance_work_by,
          iso_requested_by_name: requestForm.requested_by_name,
          iso_requested_by_designation: requestForm.requested_by_designation,
        };

        responsePayload = permitId
          ? await updateIsolationRequest(permitId, payload)
          : await createIsolationPermit(payload);

        finalPermitId = responsePayload.permit_id || permitId;
      }

      if (step === "isolation_certificate") {
        responsePayload = await submitIsolationCertificate(permitId, {
          cert_date: formatForApi(isolationCertForm.cert_date_time),
          cert_time: formatForApi(isolationCertForm.cert_date_time),
          certified_equipment_circuit_no:
            isolationCertForm.certified_equipment_circuit_no,
          ka_of: isolationCertForm.ka_of,
          isolation_actions_taken: isolationCertForm.isolation_actions_taken,
          danger_tag_put: isolationCertForm.danger_tag_put,
          lockout_tag_no: isolationCertForm.lockout_tag_no,
          authorized_person_name: isolationCertForm.authorized_person_name,
          authorized_person_designation:
            isolationCertForm.authorized_person_designation,
        });
      }

      if (step === "energisation_request") {
        responsePayload = await submitEnergisationRequest(permitId, {
          enrg_request_datetime: formatForApi(
            energisationRequestForm.request_date_time,
          ),
          enrg_department_section: energisationRequestForm.department_section,
          enrg_equipment_number: energisationRequestForm.equipment_number,
          enrg_equipment_circuit_name:
            energisationRequestForm.equipment_circuit_name,
          work_completed_permits_closed:
            energisationRequestForm.work_completed_permits_closed,
          enrg_requested_by_name: energisationRequestForm.requested_by_name,
          enrg_requested_by_designation:
            energisationRequestForm.requested_by_designation,
        });
      }

      if (step === "energisation_certificate") {
        responsePayload = await submitEnergisationCertificate(permitId, {
          cert_date: formatForApi(energisationCertForm.cert_date_time),
          cert_time: formatForApi(energisationCertForm.cert_date_time),
          certified_equipment_circuit_no:
            energisationCertForm.certified_equipment_circuit_no,
          ka_of: energisationCertForm.ka_of,
          electrically_energized: energisationCertForm.electrically_energized,
          danger_tag_removed: energisationCertForm.danger_tag_removed,
          recorded_in_shift_logbook:
            energisationCertForm.recorded_in_shift_logbook,
          authorized_person_name: energisationCertForm.authorized_person_name,
          authorized_person_designation:
            energisationCertForm.authorized_person_designation,
        });
      }

      showSnackbar(responsePayload.message || "Saved successfully");

      const nextPermitId = responsePayload.permit_id || finalPermitId;
      if (nextPermitId) {
        setTimeout(() => {
          navigate(config.nextPath(nextPermitId));
        }, 800);
      }
    } catch (error) {
      showSnackbar(getApiErrorMessage(error, "Failed to save permit step"), "error");
    } finally {
      setIsSaving(false);
    }
  };

  const renderSummary = () => {
    if (!permitData?.permit) return null;

    return (
      <div className="project-detailss" style={{ paddingBottom: "16px" }}>
        <div className="project-detail-arround">
          <div className="project-details-left-side">
            {topSummary.map((item) => {
              const statusStyle = STATUS_STYLES[currentStatus] || null;
              return (
                <div className="project-detail" key={item.label}>
                  <div className="detail-header">{item.label}:</div>
                  <div
                    className="detail-part-voyage"
                    style={
                      item.label === "Status" && statusStyle
                        ? {
                            color: statusStyle.color,
                            background: statusStyle.background,
                            padding: "2px 8px",
                            borderRadius: "999px",
                          }
                        : undefined
                    }
                  >
                    {item.value}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const renderFieldError = (field) =>
    errors[field] ? <div className="error-message">{errors[field]}</div> : null;

  const getSelectedEquipmentOption = (value) => {
    if (!value) return null;

    return (
      equipmentTagOptions.find((option) => String(option.value) === String(value)) || {
        value,
        label: value,
      }
    );
  };

  const renderRequestForm = () => (
    <>
      {shouldShowDepartmentField && (
        <div className="form-group-select isolation-request-select-field">
          <label htmlFor="department_id">
            Department<span className="text-danger">*</span>
          </label>
          <Select
            inputId="department_id"
            className="isolation-page-select"
            classNamePrefix="isolation-select"
            options={departmentOptions}
            {...selectA11yProps}
            value={selectedDepartment}
            onChange={(selectedOption) => {
              setSelectedDepartment(selectedOption);
              setCommonForm((prev) => ({
                ...prev,
                department_id: selectedOption?.value || "",
              }));
              clearError("department_id");
            }}
            placeholder="Select department"
          />
          {renderFieldError("department_id")}
        </div>
      )}

      <div className="form-group isolation-request-field">
        <label htmlFor="facility_location">
          Facility Location<span className="text-danger">*</span>
        </label>
        <div className="input-group">
          <input
            type="text"
            className="form-control"
            name="facility_location"
            value={commonForm.facility_location}
            onChange={handleCommonInputChange}
            placeholder="Enter facility location"
          />
        </div>
        {renderFieldError("facility_location")}
      </div>

      <div className="form-group isolation-request-field">
        <label htmlFor="request_date_time">
          Request Date & Time<span className="text-danger">*</span>
        </label>
        <div className="input-group">
          <DatePicker
            selected={requestForm.request_date_time}
            onChange={(date) => {
              setRequestForm((prev) => ({
                ...prev,
                request_date_time: date,
              }));
              clearError("request_date_time");
            }}
            showTimeSelect
            dateFormat="dd/MM/yyyy hh:mm aa"
            placeholderText="Select request date and time"
            className="form-control"
          />
        </div>
        {renderFieldError("request_date_time")}
      </div>

      <div className="form-group isolation-request-field">
        <label htmlFor="department_section">
          Department / Section / Area<span className="text-danger">*</span>
        </label>
        <div className="input-group">
          <input
            type="text"
            className="form-control"
            name="department_section"
            value={requestForm.department_section}
            onChange={handleRequestInputChange}
            placeholder="Enter department, section or area"
          />
        </div>
        {renderFieldError("department_section")}
      </div>

      <div className="form-group isolation-request-select-field">
        <label htmlFor="equipment_number">
          Equipment Number<span className="text-danger">*</span>
        </label>
        <Select
          inputId="equipment_number"
          className="isolation-page-select"
          classNamePrefix="isolation-select"
          options={equipmentTagOptions}
          {...selectA11yProps}
          value={getSelectedEquipmentOption(requestForm.equipment_number)}
          onChange={(selectedOption) => {
            setRequestForm((prev) => ({
              ...prev,
              equipment_number: selectedOption?.value || "",
            }));
            clearError("equipment_number");
          }}
          placeholder="Select equipment number"
        />
        {renderFieldError("equipment_number")}
      </div>

      <div className="form-group isolation-request-field">
        <label htmlFor="equipment_circuit_name">
          Equipment / Circuit Name<span className="text-danger">*</span>
        </label>
        <div className="input-group">
          <input
            type="text"
            className="form-control"
            name="equipment_circuit_name"
            value={requestForm.equipment_circuit_name}
            onChange={handleRequestInputChange}
            placeholder="Enter equipment or circuit name"
          />
        </div>
        {renderFieldError("equipment_circuit_name")}
      </div>

      <div className="form-group isolation-request-field">
        <label htmlFor="maintenance_work_by">
          Maintenance Work By<span className="text-danger">*</span>
        </label>
        <div className="input-group">
          <input
            type="text"
            className="form-control"
            name="maintenance_work_by"
            value={requestForm.maintenance_work_by}
            onChange={handleRequestInputChange}
            placeholder="Enter maintenance work by"
          />
        </div>
        {renderFieldError("maintenance_work_by")}
      </div>

      <div className="form-group isolation-request-field">
        <label htmlFor="requested_by_name">
          Requested By Name<span className="text-danger">*</span>
        </label>
        <div className="input-group">
          <input
            type="text"
            className="form-control"
            name="requested_by_name"
            value={requestForm.requested_by_name}
            onChange={handleRequestInputChange}
            placeholder="Enter requester name"
          />
        </div>
        {renderFieldError("requested_by_name")}
      </div>

      <div className="form-group isolation-request-field">
        <label htmlFor="requested_by_designation">
          Requested By Designation<span className="text-danger">*</span>
        </label>
        <div className="input-group">
          <input
            type="text"
            className="form-control"
            name="requested_by_designation"
            value={requestForm.requested_by_designation}
            onChange={handleRequestInputChange}
            placeholder="Enter requester designation"
          />
        </div>
        {renderFieldError("requested_by_designation")}
      </div>
    </>
  );

  const renderIsolationCertificateForm = () => (
    <>
      <div className="form-group isolation-cert-field">
        <label htmlFor="cert_date_time">
          Certificate Date & Time<span className="text-danger">*</span>
        </label>
        <div className="input-group">
          <DatePicker
            selected={isolationCertForm.cert_date_time}
            onChange={(date) => {
              setIsolationCertForm((prev) => ({
                ...prev,
                cert_date_time: date,
              }));
              clearError("cert_date_time");
            }}
            showTimeSelect
            dateFormat="dd/MM/yyyy hh:mm aa"
            placeholderText="Select certificate date and time"
            className="form-control"
          />
        </div>
        {renderFieldError("cert_date_time")}
      </div>

      <div className="form-group isolation-cert-field">
        <label htmlFor="certified_equipment_circuit_no">
          Certified Equipment / Circuit No.<span className="text-danger">*</span>
        </label>
        <div className="input-group">
          <input
            type="text"
            className="form-control"
            name="certified_equipment_circuit_no"
            value={isolationCertForm.certified_equipment_circuit_no}
            onChange={handleIsolationCertInputChange}
            placeholder="Enter certified equipment / circuit no."
          />
        </div>
        {renderFieldError("certified_equipment_circuit_no")}
      </div>

      <div className="form-group isolation-cert-field">
        <label htmlFor="ka_of">
          KA Of<span className="text-danger">*</span>
        </label>
        <div className="input-group">
          <input
            type="text"
            className="form-control"
            name="ka_of"
            value={isolationCertForm.ka_of}
            onChange={handleIsolationCertInputChange}
            placeholder="Enter KA of"
          />
        </div>
        {renderFieldError("ka_of")}
      </div>

      <div className="form-group isolation-form-span-2 isolation-cert-field isolation-cert-actions">
        <label>Isolation Actions Taken</label>
        <div className="isolation-checkbox-group">
          {ISOLATION_ACTION_OPTIONS.map((option) => (
            <label key={option.value} className="isolation-checkbox-option">
              <input
                type="checkbox"
                checked={isolationCertForm.isolation_actions_taken.includes(
                  option.value,
                )}
                onChange={(event) => {
                  setIsolationCertForm((prev) => ({
                    ...prev,
                    isolation_actions_taken: event.target.checked
                      ? [...prev.isolation_actions_taken, option.value]
                      : prev.isolation_actions_taken.filter(
                          (item) => item !== option.value,
                        ),
                  }));
                }}
              />
              {option.label}
            </label>
          ))}
        </div>
      </div>

      <div className="form-group isolation-cert-field isolation-cert-lockout">
        <label htmlFor="lockout_tag_no">Lockout Tag No.</label>
        <div className="input-group">
          <input
            type="text"
            className="form-control"
            name="lockout_tag_no"
            value={isolationCertForm.lockout_tag_no}
            onChange={handleIsolationCertInputChange}
            placeholder="Enter lockout tag number"
          />
        </div>
      </div>

      <div className="form-group isolation-cert-toggle">
        <label className="isolation-checkbox-option isolation-cert-toggle-label">
          <input
            type="checkbox"
            name="danger_tag_put"
            checked={isolationCertForm.danger_tag_put}
            onChange={handleIsolationCertInputChange}
          />
          Danger Tag Put
        </label>
      </div>

      <div className="form-group isolation-cert-field">
        <label htmlFor="authorized_person_name">
          Authorised Person Name<span className="text-danger">*</span>
        </label>
        <div className="input-group">
          <input
            type="text"
            className="form-control"
            name="authorized_person_name"
            value={isolationCertForm.authorized_person_name}
            onChange={handleIsolationCertInputChange}
            placeholder="Enter authorised person name"
          />
        </div>
        {renderFieldError("authorized_person_name")}
      </div>

      <div className="form-group isolation-cert-field">
        <label htmlFor="authorized_person_designation">
          Authorised Person Designation<span className="text-danger">*</span>
        </label>
        <div className="input-group">
          <input
            type="text"
            className="form-control"
            name="authorized_person_designation"
            value={isolationCertForm.authorized_person_designation}
            onChange={handleIsolationCertInputChange}
            placeholder="Enter authorised person designation"
          />
        </div>
        {renderFieldError("authorized_person_designation")}
      </div>
    </>
  );

  const renderEnergisationRequestForm = () => (
    <>
      <div className="form-group energisation-request-field">
        <label htmlFor="energisation_request_date_time">
          Request Date & Time<span className="text-danger">*</span>
        </label>
        <div className="input-group">
          <DatePicker
            selected={energisationRequestForm.request_date_time}
            onChange={(date) => {
              setEnergisationRequestForm((prev) => ({
                ...prev,
                request_date_time: date,
              }));
              clearError("energisation_request_date_time");
            }}
            showTimeSelect
            dateFormat="dd/MM/yyyy hh:mm aa"
            placeholderText="Select request date and time"
            className="form-control"
          />
        </div>
        {renderFieldError("energisation_request_date_time")}
      </div>

      <div className="form-group energisation-request-field">
        <label htmlFor="department_section">
          Department / Section / Area<span className="text-danger">*</span>
        </label>
        <div className="input-group">
          <input
            type="text"
            className="form-control"
            name="department_section"
            value={energisationRequestForm.department_section}
            onChange={handleEnergisationRequestInputChange}
            placeholder="Enter department, section or area"
          />
        </div>
        {renderFieldError("enrg_department_section")}
      </div>

      <div className="form-group energisation-request-field">
        <label htmlFor="equipment_number">
          Equipment Number<span className="text-danger">*</span>
        </label>
        <Select
          inputId="energisation_equipment_number"
          className="isolation-page-select"
          classNamePrefix="isolation-select"
          options={equipmentTagOptions}
          {...selectA11yProps}
          value={getSelectedEquipmentOption(energisationRequestForm.equipment_number)}
          onChange={(selectedOption) => {
            setEnergisationRequestForm((prev) => ({
              ...prev,
              equipment_number: selectedOption?.value || "",
            }));
            clearError("enrg_equipment_number");
          }}
          placeholder="Select equipment number"
        />
        {renderFieldError("enrg_equipment_number")}
      </div>

      <div className="form-group energisation-request-field">
        <label htmlFor="equipment_circuit_name">
          Equipment / Circuit Name<span className="text-danger">*</span>
        </label>
        <div className="input-group">
          <input
            type="text"
            className="form-control"
            name="equipment_circuit_name"
            value={energisationRequestForm.equipment_circuit_name}
            onChange={handleEnergisationRequestInputChange}
            placeholder="Enter equipment or circuit name"
          />
        </div>
        {renderFieldError("enrg_equipment_circuit_name")}
      </div>

      <div className="form-group energisation-request-toggle">
        <label className="isolation-checkbox-option energisation-request-toggle-label">
          <input
            type="checkbox"
            name="work_completed_permits_closed"
            checked={energisationRequestForm.work_completed_permits_closed}
            onChange={handleEnergisationRequestInputChange}
          />
          Work Completed And Permits Closed
        </label>
      </div>

      <div className="form-group energisation-request-field">
        <label htmlFor="requested_by_name">
          Requested By Name<span className="text-danger">*</span>
        </label>
        <div className="input-group">
          <input
            type="text"
            className="form-control"
            name="requested_by_name"
            value={energisationRequestForm.requested_by_name}
            onChange={handleEnergisationRequestInputChange}
            placeholder="Enter requester name"
          />
        </div>
        {renderFieldError("enrg_requested_by_name")}
      </div>

      <div className="form-group energisation-request-field">
        <label htmlFor="requested_by_designation">
          Requested By Designation<span className="text-danger">*</span>
        </label>
        <div className="input-group">
          <input
            type="text"
            className="form-control"
            name="requested_by_designation"
            value={energisationRequestForm.requested_by_designation}
            onChange={handleEnergisationRequestInputChange}
            placeholder="Enter requester designation"
          />
        </div>
        {renderFieldError("enrg_requested_by_designation")}
      </div>
    </>
  );

  const renderEnergisationCertificateForm = () => (
    <>
      <div className="form-group energisation-cert-field">
        <label htmlFor="energisation_cert_date_time">
          Certificate Date & Time<span className="text-danger">*</span>
        </label>
        <div className="input-group">
          <DatePicker
            selected={energisationCertForm.cert_date_time}
            onChange={(date) => {
              setEnergisationCertForm((prev) => ({
                ...prev,
                cert_date_time: date,
              }));
              clearError("energisation_cert_date_time");
            }}
            showTimeSelect
            dateFormat="dd/MM/yyyy hh:mm aa"
            placeholderText="Select certificate date and time"
            className="form-control"
          />
        </div>
        {renderFieldError("energisation_cert_date_time")}
      </div>

      <div className="form-group energisation-cert-field">
        <label htmlFor="certified_equipment_circuit_no">
          Certified Equipment / Circuit No.<span className="text-danger">*</span>
        </label>
        <div className="input-group">
          <input
            type="text"
            className="form-control"
            name="certified_equipment_circuit_no"
            value={energisationCertForm.certified_equipment_circuit_no}
            onChange={handleEnergisationCertInputChange}
            placeholder="Enter certified equipment / circuit no."
          />
        </div>
        {renderFieldError("enrg_certified_equipment_circuit_no")}
      </div>

      <div className="form-group energisation-cert-field">
        <label htmlFor="ka_of">
          KA Of<span className="text-danger">*</span>
        </label>
        <div className="input-group">
          <input
            type="text"
            className="form-control"
            name="ka_of"
            value={energisationCertForm.ka_of}
            onChange={handleEnergisationCertInputChange}
            placeholder="Enter KA of"
          />
        </div>
        {renderFieldError("enrg_ka_of")}
      </div>

      <div className="form-group energisation-cert-toggle">
        <label className="isolation-checkbox-option energisation-cert-toggle-label">
          <input
            type="checkbox"
            name="electrically_energized"
            checked={energisationCertForm.electrically_energized}
            onChange={handleEnergisationCertInputChange}
          />
          Electrically Energized
        </label>
      </div>

      <div className="form-group energisation-cert-toggle">
        <label className="isolation-checkbox-option energisation-cert-toggle-label">
          <input
            type="checkbox"
            name="danger_tag_removed"
            checked={energisationCertForm.danger_tag_removed}
            onChange={handleEnergisationCertInputChange}
          />
          Danger Tag Removed
        </label>
      </div>

      <div className="form-group energisation-cert-toggle">
        <label className="isolation-checkbox-option energisation-cert-toggle-label">
          <input
            type="checkbox"
            name="recorded_in_shift_logbook"
            checked={energisationCertForm.recorded_in_shift_logbook}
            onChange={handleEnergisationCertInputChange}
          />
          Recorded In Shift Logbook
        </label>
      </div>

      <div className="form-group energisation-cert-field">
        <label htmlFor="authorized_person_name">
          Authorised Person Name<span className="text-danger">*</span>
        </label>
        <div className="input-group">
          <input
            type="text"
            className="form-control"
            name="authorized_person_name"
            value={energisationCertForm.authorized_person_name}
            onChange={handleEnergisationCertInputChange}
            placeholder="Enter authorised person name"
          />
        </div>
        {renderFieldError("enrg_authorized_person_name")}
      </div>

      <div className="form-group energisation-cert-field">
        <label htmlFor="authorized_person_designation">
          Authorised Person Designation<span className="text-danger">*</span>
        </label>
        <div className="input-group">
          <input
            type="text"
            className="form-control"
            name="authorized_person_designation"
            value={energisationCertForm.authorized_person_designation}
            onChange={handleEnergisationCertInputChange}
            placeholder="Enter authorised person designation"
          />
        </div>
        {renderFieldError("enrg_authorized_person_designation")}
      </div>
    </>
  );

  const renderActiveForm = () => {
    if (step === "request") return renderRequestForm();
    if (step === "isolation_certificate") {
      return renderIsolationCertificateForm();
    }
    if (step === "energisation_request") {
      return renderEnergisationRequestForm();
    }
    return renderEnergisationCertificateForm();
  };

  if (isLoading) {
    return (
      <div className="loader-container">
        <div className="loader"></div>
        <div>Loading isolation permit workflow...</div>
      </div>
    );
  }

  return (
    <>
      <div
        className={`data-form-content-container page-content ${
          isSidebarExpanded ? "expanded" : "collapsed"
        }`}
      >
        <div className="data-form-content">
          <div className="data-form-header">
            <div className="data-form-header-content">
              <div className="data-form-heading-content-left-side">
                <Tooltip title="Back" arrow>
                  <IoMdArrowBack
                    className="back-company"
                    onClick={() => navigate(ISOLATION_LIST_ROUTE)}
                  />
                </Tooltip>
                {config.title}
              </div>
            </div>
          </div>
          <div className="header-separator"></div>
          <div className="data-form-body">
            {renderSummary()}

            {!isCurrentStepEditable && permitId ? (
              <div className="no-users-container" style={{ marginBottom: "16px" }}>
                <div className="no-users-message">
                  This step is no longer editable because the permit is in{" "}
                  {formatStatusLabel(currentStatus)} status.
                </div>
              </div>
            ) : null}

            <form className="form-detail">
              <div className="new-data-form isolation-form-grid">
                {renderActiveForm()}
              </div>
            </form>
          </div>
          <div className="footer">
            <div className="button-group">
              <button
                className="request-button"
                type="button"
                onClick={handleSave}
                disabled={isSaving || !isCurrentStepEditable}
              >
                {isSaving ? "Saving..." : "Save And Continue"}
              </button>
              <button
                className="data-form-cancel-button"
                type="button"
                onClick={() => navigate(ISOLATION_LIST_ROUTE)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        style={{ marginTop: "35px", marginLeft: "20px" }}
      >
        <MuiAlert onClose={handleSnackbarClose} severity={snackbarSeverity}>
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </>
  );
}

export default IsolationPermitStepPage;
